import { useEffect, useId, useState } from 'react';
import { Button } from '../../ui/Button';
import { Sheet } from '../../ui/Sheet';
import { Switch } from '../../ui/Switch';
import { adminApi, adminErrorText, type AppSettings } from './adminApi';

function Row({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange(v: boolean): void;
}) {
  const id = useId();
  return (
    <li className="flex items-center gap-3 rounded-[24px] border border-divider bg-surface-2 p-4">
      <div className="flex-1">
        <p className="font-medium">{label}</p>
        <p id={id} className="text-sm text-muted">
          {description}
        </p>
      </div>
      <Switch label={label} checked={checked} describedBy={id} onCheckedChange={onChange} />
    </li>
  );
}

export function AppSettingsPanel() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmBilling, setConfirmBilling] = useState(false);

  useEffect(() => {
    adminApi.getSettings().then(setSettings, (err: unknown) => setError(adminErrorText(err)));
  }, []);

  async function update(patch: Partial<AppSettings>) {
    if (!settings) return;
    const previous = settings;
    setSettings({ ...settings, ...patch });
    try {
      setSettings(await adminApi.updateSettings(patch));
    } catch (err) {
      setSettings(previous);
      setError(adminErrorText(err));
    }
  }

  if (!settings) {
    return error ? (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    ) : (
      <p role="status" className="text-muted">
        Loading settings…
      </p>
    );
  }
  return (
    <>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        <Row
          label="Invite-only sign-up"
          description="Only emails on the invite list can sign up."
          checked={settings.inviteOnly}
          onChange={(v) => void update({ inviteOnly: v })}
        />
        <Row
          label="Billing (live payments)"
          description="Leave this off until launch. When it's on, paid modules need a subscription."
          checked={settings.billingLive}
          onChange={(v) => (v ? setConfirmBilling(true) : void update({ billingLive: false }))}
        />
      </ul>
      <Sheet
        open={confirmBilling}
        onOpenChange={setConfirmBilling}
        title="Turn on live payments?"
        description="People without a plan lose access to paid modules unless you've granted them."
      >
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={async () => {
              setConfirmBilling(false);
              await update({ billingLive: true });
            }}
          >
            Turn on
          </Button>
          <Button onClick={() => setConfirmBilling(false)}>Cancel</Button>
        </div>
      </Sheet>
    </>
  );
}
